import React, { PropsWithChildren, useEffect } from 'react';

import { useOptionalRouter } from '../hooks/useOptionalRouter';
import { useUserContext } from './user-context';

type VisitedPages = {
  lastPage: { url: string; title: string };
  penultimatePage: { url: string; title: string };
};

type VisitedPagesContextValue = {
  getVisitedPages(): VisitedPages;
};

const VistedPages = React.createContext<VisitedPagesContextValue | null>(null);

export { VistedPages };

export const VISITED_PAGES = 'spk.visited.pages';

export const VistedPagesProvider: React.FC<PropsWithChildren<unknown>> = ({ children }) => {
  const { hasMarketingConsent } = useUserContext();
  const router = useOptionalRouter();

  const clearVisitedPages = () => window.sessionStorage.removeItem(VISITED_PAGES);

  const saveVisitedPage = () => {
    const visitedPages = JSON.parse(window.sessionStorage.getItem(VISITED_PAGES) || '[]');
    visitedPages.push({
      url: location.href,
      title: document.title,
    });
    if (visitedPages.length > 2) {
      visitedPages.shift();
    }
    sessionStorage.setItem(VISITED_PAGES, JSON.stringify(visitedPages));
  };

  const getVisitedPages = () => {
    if (!hasMarketingConsent) {
      return null;
    }

    const visitedPages = JSON.parse(window.sessionStorage.getItem(VISITED_PAGES) || '[]');
    visitedPages.reverse();

    if (visitedPages.length === 2 && JSON.stringify(visitedPages[0]) === JSON.stringify(visitedPages[1])) {
      return {
        penultimatePage: null,
        lastPage: visitedPages.at(0),
      };
    }

    return {
      penultimatePage: visitedPages.at(1) || null,
      lastPage: visitedPages.at(0) || null,
    };
  };

  useEffect(() => {
    if (hasMarketingConsent) {
      saveVisitedPage();
    }

    if (hasMarketingConsent === false) {
      clearVisitedPages();
    }
  }, [hasMarketingConsent]);

  useEffect(() => {
    const handleRouteChange = () => {
      if (hasMarketingConsent) {
        saveVisitedPage();
      }
    };

    router.events.on('routeChangeComplete', handleRouteChange);
    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
    };
  }, [router, hasMarketingConsent]);

  return <VistedPages.Provider value={{ getVisitedPages }}>{children}</VistedPages.Provider>;
};

export const useVisitedPages = (): VisitedPagesContextValue | null => {
  return React.useContext(VistedPages);
};
